/**
 * File Library Models
 * Type definitions for the file library state management
 */

import { FileLibrarySearchFilter } from '../file-library-search-filters/file-library-search-filters.model';
import { FILE_TYPE_FILTER_VALUES, getExtensionsForFileType } from './file-type.constants';
// Re-export types from API for consistency
import {
	ThumbnailMetadata,
	ThumbnailFormat,
	ThumbnailSize,
	ThumbnailInfo,
	ThumbnailMap
} from '../../../../../../api/src/_core/third-party/file-library/file-library.types';

export type { ThumbnailMetadata, ThumbnailFormat, ThumbnailSize, ThumbnailInfo, ThumbnailMap };

/**
 * Folder tree node for navigation
 */
export interface FolderTreeNode {
	id: string;
	name: string;
	description?: string;
	path: string;
	fullPath: string;
	children: FolderTreeNode[];
	createdAt?: string;
	updatedAt?: string;
}

/**
 * File system node representing a file or directory
 */
export interface FileSystemNode {
	id: string;
	slug: string;
	name: string;
	originalName?: string;
	description: string;
	extension: string | null;
	mimeType: string | null;
	userName: string;
	path: string;
	type: 'file' | 'directory';
	size: number | null;
	createdAt: string;
	updatedAt?: string;
	version: number;
	tags: string[];
	isStarred?: boolean;
	// Upload progress tracking
	isUploading?: boolean;
	uploadProgress?: number; // 0-100
	directoryId?: string | null; // Directory where file is being uploaded
	thumbnails?: ThumbnailMetadata;
	// Media metadata (provided by File Library API when available)
	/** Image/video width in pixels */
	width?: number;
	/** Image/video height in pixels */
	height?: number;
	/** Video/audio duration in seconds */
	duration?: number;
	/** Video frame rate (e.g., 29.97, 30, 60) */
	frameRate?: number;
	/** Rich media metadata from File Library API (video/image technical details) */
	metadata?: Record<string, unknown>;
	/** Custom metadata key-value pairs (stored in VYC-local database via attributes API) */
	customMetadata?: Record<string, unknown>;
}

/**
 * Paginated directory content response
 */
export interface PaginatedDirectoryContent {
	items: FileSystemNode[];
	page: number;
	perPage: number;
	totalItems: number;
	hasMore: boolean;
}

/**
 * Uploading file placeholder
 */
export interface UploadingFile {
	tempId: string;
	file: FileSystemNode;
	directoryId: string | null;
}

/**
 * Completed upload result for tracking success/failure
 */
export interface CompletedUpload {
	tempId: string;
	fileName: string;
	directoryId: string | null;
	success: boolean;
	completedAt: number;
}

/**
 * File library state interface
 */
export interface FileLibraryState {
	directoryTree: FolderTreeNode[];
	directoryContent: FileSystemNode[];
	selectedDirectoryId: string | null;
	uploadingFiles: UploadingFile[];
	completedUploads: CompletedUpload[];
	loading: boolean;
	loadingContent: boolean;
	loadingMore: boolean; // TRUE = appending to existing content (pagination)
	error: string | null;
	// Pagination state
	currentPage: number;
	perPage: number;
	totalItems: number;
	hasMore: boolean;
	// Collections state
	readonly collections: readonly FileCollection[];
	readonly selectedCollectionId: string | null;
	readonly loadingCollections: boolean;
	// Granular error states for sidebar sections
	readonly treeError: string | null;
	readonly collectionsError: string | null;
	// Content cache state - persists across component recreation
	contentCacheKey: string | null;
	contentCacheTimestamp: number;
	// Filter state (Phase 1)
	readonly activeFilters: ActiveFilters;
	readonly filterOptions: FilterOptions | null;
	readonly loadingFilterOptions: boolean;
	// Folder permissions — user's role on the currently selected folder, hydrated from the
	// `userRole` field on getFolderContent responses when FOLDER_GRANTS_PROXY is on.
	currentFolderUserRole: 'viewer' | 'admin' | null;
	/** Admin-configured search filters loaded for the file library */
	readonly savedSearchFilters: FileLibrarySearchFilter[] | null;
	readonly loadingSavedSearchFilters: boolean;
}

/**
 * Direct-to-Storage (DTS) Upload Interfaces
 */

export interface InitiateUploadRequest {
	filename: string;
	contentType: string;
	size: number;
	strategy: 'post' | 'multipart';
	parts?: number;
	directoryId?: string;
	storageProviderId?: string;
	/**
	 * Optional folder upload session ID this upload belongs to.
	 */
	sessionId?: string;
	/**
	 * Relative path from the folder root for this file (for folder uploads),
	 * e.g. "sub1/sub2/file.txt".
	 */
	relativePath?: string;
	/**
	 * Initial status for the uploaded file.
	 * If provided, overrides the default role-based status assignment.
	 */
	initialStatus?: 'pending' | 'published' | 'approved';
}

export interface PostUploadResponse {
	uploadId: string;
	key: string;
	strategy: 'post';
	url: string;
	fields: Record<string, string>;
	expiresInSeconds: number;
}

export interface MultipartUploadPart {
	number: number;
	url: string;
}

export interface MultipartUploadResponse {
	uploadId: string;
	key: string;
	strategy: 'multipart';
	parts: MultipartUploadPart[];
	expiresInSeconds: number;
}

export type InitiateUploadResponse = PostUploadResponse | MultipartUploadResponse;

export interface UploadPartResult {
	number: number;
	eTag: string;
	checksumSHA256?: string;
}

export interface CompleteUploadRequest {
	uploadId: string;
	key: string;
	parts?: UploadPartResult[];
}

export interface CreateUploadSessionRequest {
	rootDirectoryId?: string;
	storageProviderId?: string;
	maxFiles?: number;
	maxTotalSize?: number;
}

export interface CreateUploadSessionResponse {
	sessionId: string;
	maxFiles?: number;
	maxTotalSize?: number;
	expiresInSeconds: number;
}

// Sentinel values
export const rootFolderId = '__root'; // this is the id of the root folder
export const starredCollectionId = '__starred__'; // this id is used to identify the starred collection

/**
 * Collection type enum - matches backend enum from vml-file-library
 * STARRED is system-managed, CUSTOM is user-created
 */
export enum FileCollectionType {
	STARRED = 'starred',
	CUSTOM = 'custom'
}

/**
 * File collection entity - represents a virtual grouping of files
 * Collections allow organizing files across folders without moving them
 */
export interface FileCollection {
	readonly id: string;
	readonly name: string;
	readonly type: FileCollectionType;
	readonly createdAt: string;
	readonly itemCount?: number;
}

/**
 * Result of batch operations (add multiple items to collection)
 * Enables partial success handling with clear feedback
 */
export interface BatchOperationResult {
	readonly succeeded: readonly string[];
	readonly failed: ReadonlyArray<{ nodeId: string; error: string }>;
	readonly totalAttempted: number;
	readonly totalSucceeded: number;
}

/**
 * Search Interfaces
 */

export enum SortField {
	CREATED = 'created',
	NAME = 'name',
	SIZE = 'size',
	UPDATED = 'updatedAt',
	RELEVANCE = 'relevance'
}

export enum SortOrder {
	ASC = 'ASC',
	DESC = 'DESC'
}

export interface MetadataSearchItem {
	searchKey: string;
	searchValue: string | number | boolean;
}

export interface SearchFileSystemRequest {
	query?: string;
	directoryId?: string;
	collectionId?: string;
	page?: number;
	limit?: number;
	// Advanced fields
	types?: string[];
	extensions?: string[];
	tags?: string[];
	statuses?: string[];
	metadataSearch?: MetadataSearchItem[];
	userId?: string;
	createdAfter?: string;
	createdBefore?: string;
	sortBy?: SortField;
	sortOrder?: SortOrder;
	latestVersionOnly?: boolean;
	starred?: boolean;
}

export interface SuggestFileSystemRequest {
	prefix: string;
	directoryId?: string;
	limit?: number;
}

export interface SearchResult<T> {
	items: T[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
	hasNext: boolean;
	hasPrev: boolean;
}

/**
 * Upload result structure containing tempId and observable info
 */
export interface UploadResult {
	tempId: string;
	uploadId?: string;
}

/**
 * Upload signal describing the outcome of an upload flow.
 * - `file`: a non-session upload finished; the node is ready and any UI can refresh.
 * - `session`: the file was uploaded as part of a folder/session; finalization happens later.
 */
export type UploadSignal = { kind: 'file'; node: FileSystemNode } | { kind: 'session' };

/**
 * Upload Progress Tracking Types for Phase 2
 */

/**
 * Status of an individual upload
 */
export type UploadStatus = 'pending' | 'uploading' | 'processing' | 'complete' | 'error' | 'cancelled' | 'paused';

/**
 * Upload progress information for a single file
 */
export interface UploadProgress {
	fileId: string;
	filename: string;
	bytesUploaded: number;
	totalBytes: number;
	percentage: number;
	status: UploadStatus;
	error?: string;
	startTime?: number;
	endTime?: number;
	speed?: number; // bytes per second
}

/**
 * Batch upload progress information
 */
export interface BatchUploadProgress {
	totalFiles: number;
	completedFiles: number;
	failedFiles: number;
	totalBytes: number;
	bytesUploaded: number;
	percentage: number;
	files: UploadProgress[];
}

/**
 * Folder upload progress information
 */
export interface FolderUploadProgress extends BatchUploadProgress {
	sessionId: string;
	rootDirectoryId: string;
	foldersCreated: number;
}

/**
 * Upload options for customizing upload behavior
 */
export interface UploadOptions {
	sessionId?: string;
	relativePath?: string;
	onProgress?: (progress: UploadProgress) => void;
	retryCount?: number;
	retryDelay?: number;
}

/**
 * Upload queue item for queue management
 */
export interface UploadQueueItem {
	id: string;
	file: File;
	directoryId?: string;
	options?: UploadOptions;
	status: UploadStatus;
	progress: UploadProgress;
	retryAttempts: number;
	addedAt: number;
	priority: number;
}

/**
 * Upload queue state
 */
export interface UploadQueueState {
	items: UploadQueueItem[];
	isProcessing: boolean;
	isPaused: boolean;
	concurrentLimit: number;
	activeUploads: number;
}

// ============================================================================
// Tags & Metadata Interfaces
// ============================================================================

/**
 * File node attributes stored in VYC-local database
 * Provides organization-scoped tags and custom metadata
 */
export interface FileNodeAttributes {
	id: string;
	fileNodeId: string;
	organizationId: string;
	tags: string[];
	customMetadata: Record<string, unknown>;
	createdAt: string;
	updatedAt: string;
}

/**
 * DTO for updating tags on a file node.
 * Operations are applied in order: set > add > remove
 */
export interface UpdateTagsRequest {
	/** Tags to add to existing tags */
	add?: string[];
	/** Tags to remove from existing tags */
	remove?: string[];
	/** Replace all tags with these (overrides add/remove) */
	set?: string[];
}

/**
 * DTO for bulk updating tags across multiple file nodes.
 */
export interface BulkUpdateTagsRequest extends UpdateTagsRequest {
	/** Array of file node IDs to update */
	fileNodeIds: string[];
}

/**
 * Response from bulk operations (tags, metadata, etc.)
 */
export interface BulkOperationResult {
	/** File IDs that were successfully updated */
	succeeded: string[];
	/** File IDs that failed with error messages */
	failed: Array<{ fileId: string; error: string }>;
	/** Total number of files attempted */
	totalAttempted: number;
	/** Total number of files successfully updated */
	totalSucceeded: number;
}

/**
 * DTO for updating custom metadata on a file node.
 * Performs a shallow merge with existing metadata.
 */
export interface UpdateMetadataRequest {
	/** Custom metadata key-value pairs to merge with existing metadata */
	metadata: Record<string, unknown>;
}

/**
 * Response structure for tag suggestions
 */
export interface TagSuggestion {
	tag: string;
	count?: number;
}

/**
 * Response structure for search by tags
 */
export interface SearchByTagsResponse {
	fileNodeIds: string[];
	matchMode: 'all' | 'any';
}

// ============================================================================
// Active Filters Interface
// ============================================================================

/**
 * Active filters state for file library filtering
 * Used by QuickFilterPopover and ActiveFiltersBar components
 */
export interface ActiveFilters {
	/** File extensions to filter by (e.g., ['jpg', 'png']) */
	extensions: string[];
	/** Tags to filter by */
	tags: string[];
	/** File types to filter by */
	types: ('file' | 'directory')[];
	/** Date range filter */
	dateRange: {
		from: string | null;
		to: string | null;
	};
	/** Filter for starred items only */
	starred: boolean | null;
	/** Metadata preset / custom filter selections */
	metadataSearch: MetadataSearchItem[];
	/** Year range filter (inclusive calendar years) */
	yearRange: {
		from: number | null;
		to: number | null;
	};
}

/**
 * Filter options available for selection in the filter popover
 * Populated from API data about available values
 */
export interface FilterOptions {
	/** Available tags with usage counts */
	tags: Array<{ tag: string; count: number }>;
	/** Available file extensions with usage counts */
	extensions: Array<{ extension: string; count: number }>;
}

/**
 * Create an empty ActiveFilters object
 */
export function createEmptyFilters(): ActiveFilters {
	return {
		extensions: [],
		tags: [],
		types: [],
		dateRange: { from: null, to: null },
		starred: null,
		metadataSearch: [],
		yearRange: { from: null, to: null }
	};
}

/**
 * Check if any filters are currently active
 */
export function hasActiveFilters(filters: ActiveFilters): boolean {
	return (
		filters.extensions.length > 0 ||
		filters.tags.length > 0 ||
		filters.types.length > 0 ||
		filters.dateRange.from !== null ||
		filters.dateRange.to !== null ||
		filters.starred === true ||
		filters.metadataSearch.length > 0 ||
		filters.yearRange.from !== null ||
		filters.yearRange.to !== null
	);
}

/**
 * Count the number of active filter values
 * Used for badge display on filter button
 */
export function countActiveFilters(filters: ActiveFilters): number {
	let count = 0;
	const coveredExtensions = new Set<string>();

	for (const fileType of FILE_TYPE_FILTER_VALUES) {
		const typeExtensions = getExtensionsForFileType(fileType) ?? [];

		if (typeExtensions.length && typeExtensions.every(ext => filters.extensions.includes(ext))) {
			count += 1;
			typeExtensions.forEach(ext => coveredExtensions.add(ext));
		}
	}

	for (const extension of filters.extensions) {
		if (!coveredExtensions.has(extension)) {
			count += 1;
		}
	}
	count += filters.tags.length;
	count += filters.types.length;
	if (filters.dateRange.from !== null || filters.dateRange.to !== null) count += 1;
	if (filters.starred === true) count += 1;
	count += filters.metadataSearch.length;
	if (filters.yearRange.from !== null || filters.yearRange.to !== null) count += 1;
	return count;
}
